import * as React from 'react';
import URI from 'urijs';

import Routes from 'routing/Routes';
import { Button } from 'components/bootstrap';
import { LinkContainer } from 'components/common/router';
import { ReadOnlyFormGroup, Spinner } from 'components/common';
import SectionComponent from 'components/common/Section/SectionComponent';
import useRoles from 'common/hooks/useRoles';
import type { SAMLBackendFormValues } from 'authentication/components/saml/config/types';
import useSAMLBackendDetails from 'authentication/components/saml/hooks/useSAMLBackendDetails';
import SAMLCertInfoTable from 'authentication/components/saml/config/SAMLCertInfoTable';
import { SSO_SAML_ACS_PATH, SSO_SAML_LOGOUT_PATH } from 'authentication/components/saml/constants';

interface ConfigSectionProps {
  authenticationBackend: SAMLBackendFormValues;
}

const normalizeURI = (url: string) => new URI(url).normalizePath().toString();

const ConfigValue = ({ label, value, code = false }) =>
  value && <ReadOnlyFormGroup label={label} value={code ? <code>{value}</code> : value} />;

const certDetails = (isLoading, certs) => {
  if (isLoading) {
    return <Spinner text="Loading certificate details..." />;
  }
  if (!certs) {
    return <em>Couldn&apos;t load certificate details!</em>;
  }

  return Object.keys(certs || {}).map((key) => {
    const { certificate, subject, issuer, sha1_fingerprint, sha256_fingerprint, valid_from, valid_to } = certs[key];

    return (
      <SAMLCertInfoTable
        key={sha256_fingerprint}
        certificate={certificate}
        subject={subject}
        issuer={issuer}
        sha256Fingerprint={sha256_fingerprint}
        sha1Fingerprint={sha1_fingerprint}
        validFrom={valid_from}
        validTo={valid_to}
      />
    );
  });
};

const SAMLBackendConfigSection = ({ authenticationBackend }: ConfigSectionProps) => {
  const { isLoading, roles } = useRoles();
  const {
    id,
    title,
    description,
    defaultRoles,
    idpMetadataUrl,
    idpMetadataRefreshAtLogin,
    idpSsoServicePostBindingUrl,
    idpSsoServiceRedirectBindingUrl,
    idpLogoutServicePostBindingUrl,
    idpLogoutServiceRedirectBindingUrl,
    idpEntityId,
    idpUserNameAttribute,
    idpEmailAttribute,
    idpFirstNameAttribute,
    idpLastNameAttribute,
    idpFullNameAttribute,
    spBaseUrl,
    spEntityId,
  } = authenticationBackend;

  const { isLoading: areDetailsLoading, details } = useSAMLBackendDetails(id);

  if (isLoading) {
    return <Spinner />;
  }

  const foundRoles = defaultRoles
    .split(',')
    .map((roleId) => roles.find((role) => role.id === roleId)?.name)
    .join(', ');

  const idpCertificates = certDetails(areDetailsLoading, details?.idp_certs);
  const spCertificates = certDetails(areDetailsLoading, details?.sp_certs);

  return (
    <SectionComponent
      title="Server Configuration"
      headerActions={
        <LinkContainer to={Routes.SYSTEM.AUTHENTICATION.BACKENDS.edit(id, 'idp_config')}>
          <Button bsStyle="success" bsSize="small">
            Edit
          </Button>
        </LinkContainer>
      }>
      <ReadOnlyFormGroup label="Title" value={title} />
      <ReadOnlyFormGroup label="Description" value={description} />
      <ReadOnlyFormGroup label="Default roles" value={foundRoles} />

      <hr />
      <h3>Identity Provider</h3>
      <ConfigValue label="Metadata URL" value={idpMetadataUrl} />
      <ReadOnlyFormGroup label="Metadata Refresh At Login" value={idpMetadataRefreshAtLogin} />
      <ConfigValue label="SSO Service Redirect URL" value={idpSsoServiceRedirectBindingUrl} />
      <ConfigValue label="SSO Service POST URL" value={idpSsoServicePostBindingUrl} />
      <ConfigValue label="Logout Service Redirect URL" value={idpLogoutServiceRedirectBindingUrl} />
      <ConfigValue label="Logout Service POST URL" value={idpLogoutServicePostBindingUrl} />
      <ConfigValue label="Entity ID" value={idpEntityId} />
      <ReadOnlyFormGroup label="Certificate" value={idpCertificates} />

      <hr />
      <h3>Service Provider</h3>
      <ConfigValue label="Base URL" value={spBaseUrl} />
      <ConfigValue label="Entity ID" value={spEntityId} />
      <ConfigValue
        label="Assertion Consumer Service (ACS) URL"
        value={normalizeURI(`${spBaseUrl}/${SSO_SAML_ACS_PATH}`)}
      />
      <ConfigValue label="Single Logout Service URL" value={normalizeURI(`${spBaseUrl}/${SSO_SAML_LOGOUT_PATH}`)} />
      <ReadOnlyFormGroup label="Certificate" value={spCertificates} />

      {[
        idpUserNameAttribute,
        idpEmailAttribute,
        idpFirstNameAttribute,
        idpLastNameAttribute,
        idpFullNameAttribute,
      ].some((value) => !!value) && (
        <>
          <hr />
          <h3>User Attribute Mapping</h3>
          <ConfigValue label="User Name" value={idpUserNameAttribute} />
          <ConfigValue label="Email" value={idpEmailAttribute} />
          <ConfigValue label="First Name" value={idpFirstNameAttribute} />
          <ConfigValue label="Last Name" value={idpLastNameAttribute} />
          <ConfigValue label="Full Name" value={idpFullNameAttribute} />
        </>
      )}
    </SectionComponent>
  );
};

export default SAMLBackendConfigSection;
